// Admin CRM — contact detail/edit page replicating the etoh.io form layout:
// Prénom · Nom · Email · Téléphone / Société · Poste · Langue / LinkedIn ·
// Instagram / Facebook · X / TikTok / Site Web / Pays · Catégorie · Tags · Statut.
// Plus action timeline (notes, calls, meetings…) below + Sequence panel.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Save, Plus, MessageSquare, Phone, Mail, Users, Linkedin, Instagram, Zap, Play, StopCircle, MailCheck } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { CATEGORY_LABELS } from "@/pages/admin/AdminCRMPage";

const STATUSES = ["lead", "contacted", "qualified", "customer", "lost"];
const STATUS_LABEL = { lead: "Nouveau", contacted: "Contacté", qualified: "Qualifié", customer: "Client", lost: "Perdu" };
const ACTION_KINDS = [
  { k: "note", label: "Note", Icon: MessageSquare },
  { k: "call", label: "Appel", Icon: Phone },
  { k: "email", label: "Email", Icon: Mail },
  { k: "meeting", label: "Rendez-vous", Icon: Users },
  { k: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { k: "instagram", label: "Instagram", Icon: Instagram },
];

const AdminCRMContactPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminApi.getContact(id);
        if (cancelled) return;
        // Backfill first_name / last_name from legacy `name`
        const c = data?.contact || {};
        if (!c.first_name && !c.last_name && c.name) {
          const parts = c.name.split(" ");
          c.first_name = parts.shift() || "";
          c.last_name = parts.join(" ");
        }
        c.socials = { linkedin: "", instagram: "", facebook: "", twitter: "", tiktok: "", website: "", ...(c.socials || {}) };
        setContact(c);
        setActions(data?.actions || []);
      } catch (e) {
        setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const upd = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  const updSocial = (k, v) => setContact((c) => ({ ...c, socials: { ...(c.socials || {}), [k]: v } }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!contact?.first_name?.trim()) { setErr("Prénom requis"); return; }
    setSaving(true); setErr("");
    try {
      const tagsValue = Array.isArray(contact.tags) ? contact.tags : (contact.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        email: contact.email || null,
        phone: contact.phone || "",
        company: contact.company || "",
        position: contact.position || "",
        language: contact.language || "",
        country: contact.country || "",
        category: contact.category || "",
        socials: contact.socials || {},
        tags: tagsValue,
        status: contact.status || "lead",
        notes: contact.notes || "",
      };
      const updated = await adminApi.updateContact(id, payload);
      setContact({ ...contact, ...updated });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirm("Supprimer définitivement ce contact ? Toutes les actions associées seront aussi effacées.")) return;
    await adminApi.deleteContact(id);
    navigate("/admin/crm");
  };

  if (loading) return <div className="py-20 text-center text-[color:var(--gv-ink)]/55" data-testid="contact-loading">Chargement…</div>;
  if (!contact) return <div className="py-20 text-center text-rose-600" data-testid="contact-error">{err || "Contact introuvable"}</div>;

  const tagsStr = Array.isArray(contact.tags) ? contact.tags.join(", ") : (contact.tags || "");

  return (
    <div data-testid="admin-crm-contact">
      <div className="mb-5">
        <Link to="/admin/crm" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]">
          <ArrowLeft size={13} /> Retour au CRM
        </Link>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1" data-testid="contact-name">
            {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email || "Sans nom"}
          </h1>
          <div className="text-sm text-[color:var(--gv-ink)]/60">{contact.company || "—"} · {contact.email || "—"}</div>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-[12px] text-emerald-700">✓ Enregistré</span>}
          <button onClick={onDelete} className="text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-[4px] text-sm inline-flex items-center gap-1.5" data-testid="contact-delete-btn">
            <Trash2 size={14} /> Supprimer
          </button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="contact-save-btn">
            <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 mb-8 space-y-5">
        <Row4>
          <Field label="Prénom *" v={contact.first_name} onChange={(v) => upd("first_name", v)} required testId="field-first_name" />
          <Field label="Nom" v={contact.last_name} onChange={(v) => upd("last_name", v)} testId="field-last_name" />
          <Field label="Email" type="email" v={contact.email || ""} onChange={(v) => upd("email", v)} testId="field-email" />
          <Field label="Téléphone" v={contact.phone} onChange={(v) => upd("phone", v)} testId="field-phone" />
        </Row4>
        <Row3>
          <Field label="Société" v={contact.company} onChange={(v) => upd("company", v)} testId="field-company" />
          <Field label="Poste" v={contact.position} onChange={(v) => upd("position", v)} testId="field-position" />
          <SelectField label="Langue" v={contact.language || ""} onChange={(v) => upd("language", v)} testId="field-language" options={[["", "—"], ["EN", "EN"], ["FR", "FR"], ["ES", "ES"], ["PT", "PT"]]} />
        </Row3>
        <Row2>
          <Field label="LinkedIn URL" v={contact.socials?.linkedin || ""} onChange={(v) => updSocial("linkedin", v)} testId="field-linkedin" />
          <Field label="Instagram URL" v={contact.socials?.instagram || ""} onChange={(v) => updSocial("instagram", v)} testId="field-instagram" />
        </Row2>
        <Row2>
          <Field label="Facebook URL" v={contact.socials?.facebook || ""} onChange={(v) => updSocial("facebook", v)} testId="field-facebook" />
          <Field label="X / Twitter URL" v={contact.socials?.twitter || ""} onChange={(v) => updSocial("twitter", v)} testId="field-twitter" />
        </Row2>
        <Row2>
          <Field label="TikTok URL" v={contact.socials?.tiktok || ""} onChange={(v) => updSocial("tiktok", v)} testId="field-tiktok" />
          <Field label="Site Web" v={contact.socials?.website || ""} onChange={(v) => updSocial("website", v)} testId="field-website" />
        </Row2>
        <Row4>
          <Field label="Pays" v={contact.country} onChange={(v) => upd("country", v)} testId="field-country" />
          <SelectField label="Catégorie" v={contact.category || ""} onChange={(v) => upd("category", v)} testId="field-category" options={[["", "— Sélectionner —"], ...Object.entries(CATEGORY_LABELS)]} />
          <Field label="Tags (CSV)" v={tagsStr} onChange={(v) => upd("tags", v)} testId="field-tags" />
          <SelectField label="Statut" v={contact.status || "lead"} onChange={(v) => upd("status", v)} testId="field-status" options={STATUSES.map((s) => [s, STATUS_LABEL[s]])} />
        </Row4>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">Notes</div>
          <textarea value={contact.notes || ""} onChange={(e) => upd("notes", e.target.value)} rows={4} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid="field-notes" />
        </label>
        {err && <div className="text-rose-600 text-[13px]" data-testid="contact-save-error">{err}</div>}
      </form>

      <ActionsTimeline contactId={id} actions={actions} setActions={setActions} />

      <ProspectSequencePanel contactId={id} />
    </div>
  );
};

const Row2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const Row3 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
const Row4 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>;

const Field = ({ label, v, onChange, type = "text", required, testId }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <input type={type} value={v || ""} onChange={(e) => onChange(e.target.value)} required={required} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm focus:border-[color:var(--gv-pink)]/40 outline-none" data-testid={testId} />
  </label>
);

const SelectField = ({ label, v, onChange, options, testId }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <select value={v} onChange={(e) => onChange(e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm bg-white focus:border-[color:var(--gv-pink)]/40 outline-none" data-testid={testId}>
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </label>
);

const ActionsTimeline = ({ contactId, actions, setActions }) => {
  const [adding, setAdding] = useState(null); // kind being added
  const [draft, setDraft] = useState({ title: "", body: "" });

  const submit = async () => {
    if (!adding) return;
    const a = await adminApi.addAction(contactId, { kind: adding, title: draft.title, body: draft.body });
    setActions([a, ...actions]);
    setAdding(null);
    setDraft({ title: "", body: "" });
  };

  const remove = async (actionId) => {
    if (!confirm("Supprimer cette action ?")) return;
    await adminApi.deleteAction(actionId);
    setActions(actions.filter((a) => a.id !== actionId));
  };

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6" data-testid="actions-timeline">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Activité</h2>
        <div className="flex flex-wrap items-center gap-1">
          {ACTION_KINDS.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setAdding(k)} className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-[4px] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)]" data-testid={`add-action-${k}`}>
              <Plus size={11} /> <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </div>

      {adding && (
        <div className="mb-5 p-4 border border-[color:var(--gv-pink)]/30 bg-[color:var(--gv-pink)]/5 rounded-[4px]">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">Nouvelle {ACTION_KINDS.find((a) => a.k === adding)?.label || adding}</div>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Titre (optionnel)" className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm mb-2" data-testid="action-draft-title" />
          <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={3} placeholder="Détails…" className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm mb-3" data-testid="action-draft-body" />
          <div className="flex items-center gap-2">
            <button onClick={submit} className="btn-primary text-sm" data-testid="action-draft-save">Enregistrer</button>
            <button onClick={() => { setAdding(null); setDraft({ title: "", body: "" }); }} className="text-sm text-[color:var(--gv-ink)]/60">Annuler</button>
          </div>
        </div>
      )}

      {actions.length === 0 ? (
        <div className="text-center py-8 text-[color:var(--gv-ink)]/50 text-sm">Aucune activité pour ce contact.</div>
      ) : (
        <ul className="space-y-3">
          {actions.map((a) => {
            const meta = ACTION_KINDS.find((x) => x.k === a.kind) || ACTION_KINDS[0];
            const Icon = meta.Icon;
            return (
              <li key={a.id} className="flex gap-3 p-3 rounded-[4px] border border-[color:var(--gv-line)]" data-testid={`action-${a.id}`}>
                <div className="w-8 h-8 shrink-0 rounded-[4px] bg-[color:var(--gv-mist)] flex items-center justify-center"><Icon size={14} className="text-[color:var(--gv-blue)]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-[14px] text-[color:var(--gv-ink)]">{a.title || meta.label}</div>
                    <div className="text-[11.5px] text-[color:var(--gv-ink)]/50">{formatRelative(a.at)}</div>
                  </div>
                  {a.body && <div className="text-[13px] text-[color:var(--gv-ink)]/75 mt-1 whitespace-pre-wrap">{a.body}</div>}
                </div>
                <button onClick={() => remove(a.id)} className="text-rose-500/60 hover:text-rose-600 self-start"><Trash2 size={13} /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

function formatRelative(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

// =========================================================================
// Prospect Sequence Panel (Workflow Geovinum v1 — J0 / J+5 / J+10 / J+20)
// =========================================================================

const STEP_LABELS = {
  1: "J0 — Découverte",
  2: "J+5 — Mise en situation",
  3: "J+10 — Essai gratuit",
  4: "J+20 — Intégration",
};

const ProspectSequencePanel = ({ contactId }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const d = await adminApi.getProspectSequence(contactId);
      setState(d);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  const onStart = async () => {
    if (!confirm("Démarrer (ou redémarrer) la séquence email B2B pour ce contact ? Le step J0 sera envoyé au prochain run du cron quotidien.")) return;
    setBusy(true); setErr("");
    try { await adminApi.startProspectSequence(contactId); await load(); }
    catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };
  const onStop = async () => {
    if (!confirm("Arrêter la séquence pour ce contact ?")) return;
    setBusy(true);
    try { await adminApi.stopProspectSequence(contactId); await load(); }
    finally { setBusy(false); }
  };
  const onReplied = async () => {
    if (!confirm("Marquer ce contact comme ayant répondu ? La séquence sera stoppée et Audrey notifiée.")) return;
    setBusy(true);
    try { await adminApi.markProspectReplied(contactId); await load(); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  if (!state) return null;

  const seq = state.sequence || {};
  const active = !!seq.active;
  const sent = seq.steps_sent || [];

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 mt-8" data-testid="prospect-sequence-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl text-[color:var(--gv-ink)] flex items-center gap-2">
            <Zap size={16} className="text-[color:var(--gv-pink)]" /> Séquence prospect B2B
          </h2>
          <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mt-0.5">
            Workflow Geovinum v1 · 4 emails (J0 / J+5 / J+10 / J+20) · Stop sur réponse
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!active && state.eligible && (
            <button onClick={onStart} disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="sequence-start-btn">
              <Play size={13} /> {sent.length ? "Redémarrer" : "Démarrer"}
            </button>
          )}
          {active && (
            <>
              <button onClick={onReplied} disabled={busy} className="text-sm px-3 py-1.5 rounded-[4px] border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="sequence-replied-btn">
                <MailCheck size={13} /> Marquer répondu
              </button>
              <button onClick={onStop} disabled={busy} className="text-sm px-3 py-1.5 rounded-[4px] border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="sequence-stop-btn">
                <StopCircle size={13} /> Arrêter
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <Stat label="Lead score" value={state.lead_score} pink />
        <Stat label="Statut séquence" value={active ? "Active" : (seq.stop_reason ? `Stoppée (${seq.stop_reason})` : "Non démarrée")} />
        <Stat label="Catégorie" value={state.category ? (CATEGORY_LABELS[state.category] || state.category) : "—"} />
      </div>

      {!state.eligible && (
        <div className="bg-amber-50 text-amber-800 px-3 py-2 rounded-[3px] text-[12.5px] mb-4" data-testid="sequence-ineligible">
          ⚠ Catégorie non éligible pour la séquence B2B. Catégories supportées : {state.allowed_categories.map((c) => CATEGORY_LABELS[c] || c).join(", ")}.
        </div>
      )}

      {state.tags?.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">Tags d&apos;engagement</div>
          <div className="flex flex-wrap gap-1.5" data-testid="sequence-tags">
            {state.tags.map((tag) => (
              <span key={tag} className="text-[11.5px] px-2 py-0.5 rounded-[3px] bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/80 border border-[color:var(--gv-line)]">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">Calendrier</div>
        <ul className="space-y-2" data-testid="sequence-timeline">
          {[1, 2, 3, 4].map((s) => {
            const stepDone = sent.find((x) => x.step === s);
            const isNext = active && seq.next_step === s;
            return (
              <li key={s} className={`flex items-center justify-between p-2.5 rounded-[3px] border ${stepDone ? "border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40" : isNext ? "border-[color:var(--gv-pink)]/40 bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)]"}`} data-testid={`sequence-step-${s}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${stepDone ? "bg-emerald-500 text-white" : isNext ? "bg-[color:var(--gv-pink)] text-white" : "bg-[color:var(--gv-line)] text-[color:var(--gv-ink)]/50"}`}>
                    {stepDone ? "✓" : s}
                  </span>
                  <span className="text-[13.5px] text-[color:var(--gv-ink)]">{STEP_LABELS[s]}</span>
                </div>
                <span className="text-[11.5px] text-[color:var(--gv-ink)]/55">
                  {stepDone ? `Envoyé ${formatRelative(stepDone.at)}` : isNext ? `Prochain envoi ${formatRelative(seq.next_step_at)}` : "—"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {err && <div className="text-rose-600 text-[13px] mt-3" data-testid="sequence-error">{err}</div>}
    </div>
  );
};

const Stat = ({ label, value, pink }) => (
  <div className="bg-[color:var(--gv-mist)] rounded-[4px] p-3">
    <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</div>
    <div className={`font-display text-[20px] ${pink ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"}`}>{value}</div>
  </div>
);

export default AdminCRMContactPage;
